// src/pages/Settings/company/forms/index.js
//
// Task 24711 - Forms in the Settings nav.
//
// Forms were only reachable from /form-list, which is not where anyone goes
// looking for something they set up once and then edit occasionally. This is
// the same forms, listed where the rest of the configuration lives, with a link
// straight into the builder for each one.
//
// Deliberately a READ-ONLY index rather than a second copy of the forms list
// page: publish, duplicate and delete already live on /form-list, and having
// two screens that can both destroy a form is how they end up disagreeing about
// what they do. Everything here either opens the builder or opens the form.

import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Table, Button } from "reactstrap";
import { isEmpty } from "lodash";

import "../../settings.styles.css";
import SettingsPanel from "../../components/SettingsPanel";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import HandleDateTime from "../../../../Components/Common/HandleDateTime";
import { formStatusBadge } from "../../../FormBuilder/formsUtils";
// Task 24888 - the company's own form statuses, set up next to the forms
// they apply to rather than in a settings area of their own.
import FormStatusesTab from "./FormStatusesTab";
import { listForms } from "../../../../store/forms/action";

const FormsSettings = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [activeTab, setActiveTab] = useState("forms");

  const formsList = useSelector(
    (state) =>
      state.Forms?.formsList?.form_list ||
      state.Forms?.formsList?.data?.form_list ||
      []
  );
  const loading = useSelector((state) => state.Forms?.formsListLoading);

  useEffect(() => {
    dispatch(listForms());
  }, [dispatch]);

  // Newest activity first, which is the order you want when you are looking for
  // "the one I was working on".
  const rows = useMemo(
    () =>
      [...(formsList || [])].sort((a, b) =>
        String(b.UpdatedDateTime || "").localeCompare(
          String(a.UpdatedDateTime || "")
        )
      ),
    [formsList]
  );

  return (
    <SettingsPanel
      title="Forms"
      description="Online forms, their status, and how much has come in through each one."
      tabs={[
        { value: "forms", label: "Forms", icon: "fa-solid fa-clipboard-list" },
        { value: "statuses", label: "Statuses", icon: "fa-solid fa-tags" },
      ]}
      value={activeTab}
      onChange={setActiveTab}
      actions={
        activeTab !== "forms" ? null :
        <Button
          color="primary"
          size="sm"
          onClick={() => history.push("/form-builder/new")}
        >
          <i className="fa-solid fa-plus me-2" />
          New Form
        </Button>
      }
    >
      {activeTab === "statuses" ? (
        <FormStatusesTab />
      ) : loading && isEmpty(rows) ? (
        <LoadingSpinner />
      ) : isEmpty(rows) ? (
        <div className="text-center p-4">
          <h5>No forms yet</h5>
          <p className="text-muted">
            Build one from scratch, or upload a document and have it read the
            questions off the page.
          </p>
          <Button
            color="primary"
            size="sm"
            onClick={() => history.push("/form-builder/new")}
          >
            <i className="fa-solid fa-plus me-2" />
            New Form
          </Button>
        </div>
      ) : (
        <div className="table-responsive">
          <Table className="table-sm align-middle mb-0">
            <thead className="text-muted">
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th className="text-end">Fields</th>
                <th className="text-end">Submissions</th>
                <th>Last Submission</th>
                <th className="text-end">&nbsp;</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((form) => {
                const meta = formStatusBadge(form);
                const submissions = Number(form.SubmissionCount ?? 0);

                return (
                  <tr key={form.Id}>
                    <td>
                      <Link to={`/form-builder/${form.Id}`}>{form.Name}</Link>
                    </td>

                    <td>
                      <span className={`badge ${meta.badge}`} style={meta.style}>
                        {meta.label}
                      </span>
                    </td>

                    <td className="text-end">
                      {Number(form.FieldCount ?? 0)}
                    </td>

                    <td className="text-end">
                      {submissions > 0 ? (
                        <Link to={`/form-submissions/${form.Id}`}>
                          {submissions}
                        </Link>
                      ) : (
                        <span className="text-muted">0</span>
                      )}
                    </td>

                    {/* Task 24712 - the same "when was it last used" the forms
                        list now shows. */}
                    <td>
                      {form.LastSubmissionDateTime ? (
                        HandleDateTime(form.LastSubmissionDateTime)
                      ) : (
                        <span className="text-muted">Never</span>
                      )}
                    </td>

                    <td className="text-end">
                      <Button
                        color="light"
                        size="sm"
                        className="me-2"
                        onClick={() =>
                          history.push(`/form-builder/${form.Id}`)
                        }
                      >
                        <i className="ri-pencil-fill align-bottom" />
                      </Button>

                      {/* Preview works on a draft too - /form-fill runs the
                          form as a staff member, so it does not need the form
                          to be published the way the public link does. */}
                      <Button
                        color="light"
                        size="sm"
                        onClick={() =>
                          window.open(
                            `/form-fill/${form.Id}`,
                            "_blank",
                            "noopener,noreferrer"
                          )
                        }
                      >
                        <i className="ri-eye-line align-bottom" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}
    </SettingsPanel>
  );
};

export default FormsSettings;
